import React, { useRef, useState } from "react";
import { useTinyHost } from "@tiny/host-contract";
import { closeSnackbar } from "notistack";
import { MergeAudienceDialogProps } from "./AudienceMergeDialog";
import { SegmentView } from "@tiny/api";
import { usePutMergeAudiencesQuery } from "@tiny/api";
import { showMergeMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";

export function useMergeAudienceDialog(props: MergeAudienceDialogProps) {
  const { onClose, audiences } = props;
  const { navigate } = useTinyHost();
  const [sourceAudiences, setSourceAudiences] =
    useState<SegmentView[]>(audiences);
  const [destinationAudience, setDestinationAudience] = useState<
    SegmentView | undefined
  >();
  const [draggingItem, setDraggingItem] = useState<SegmentView | null>(null);
  const [destinationAudienceName, setDestinationAudienceName] =
    useState<string>();
  const sourceParentBoxRef = useRef<HTMLDivElement | null>(null);
  const destinationParentBoxRef = useRef<HTMLDivElement | null>(null);

  const { mutateAsync: putMergeAudiences, isLoading: isAudienceMerging } =
    usePutMergeAudiencesQuery();

  const handleSourceDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    item: SegmentView
  ) => {
    setDraggingItem(item);
    e.dataTransfer.setData("text/plain", "");
  };

  const handleSourceDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleSourceDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetItem: SegmentView
  ) => {
    if (!draggingItem) return;

    const currentIndex = sourceAudiences.indexOf(draggingItem);
    const targetIndex = sourceAudiences.indexOf(targetItem);

    if (targetIndex !== -1) {
      const newItems = [...sourceAudiences];
      if (currentIndex !== -1) {
        newItems.splice(currentIndex, 1);
        newItems.splice(targetIndex, 0, draggingItem);
      } else {
        newItems.splice(targetIndex, 0, draggingItem);
      }
      setSourceAudiences(newItems);
    }
  };

  const closeMergeDialog = () => {
    setSourceAudiences([]);
    setDestinationAudience(undefined);
    setDestinationAudienceName("");
    onClose();
  };
  const handleDestinationDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (destinationAudience) {
      setDraggingItem(destinationAudience);
    }
    e.dataTransfer.setData("text/plain", "");
  };

  const handleDestinationDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (destinationParentBoxRef.current) {
      const rect = destinationParentBoxRef.current.getBoundingClientRect();
      const isOutside = e.clientY < rect.top;

      if (isOutside) {
        setDestinationAudience(undefined);
      }
    }
  };

  const handleDestinationDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDestinationDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (sourceParentBoxRef.current && sourceAudiences?.length >= 1) {
      const rect = sourceParentBoxRef.current.getBoundingClientRect();
      const isOutside = e.clientY > rect.bottom;

      if (isOutside && draggingItem) {
        const newDestinationAudience = draggingItem;
        if (!destinationAudience) {
          setSourceAudiences((prevItems) =>
            prevItems.filter((item) => item.id !== draggingItem.id)
          );
        } else {
          setSourceAudiences((prevItems) => {
            const updatedItems = [...prevItems];
            const draggingItemIndex = updatedItems.findIndex(
              (item) => item.id === draggingItem.id
            );

            if (draggingItemIndex !== -1) {
              updatedItems[draggingItemIndex] = destinationAudience;
            }
            return updatedItems;
          });
        }
        setDestinationAudience(newDestinationAudience);
      }
    }

    setDraggingItem(null);
  };

  const handleMergeAudiences = async () => {
    try {
      showMergeMessage({
        variant: "merging",
        message: "Processing",
        description: "Your audiences are merging!",
        persist: true,
        showClose: true,
      });
      closeMergeDialog();
      const newAudience = await putMergeAudiences({
        data: {
          sourceAudienceIds: sourceAudiences.map(
            (audience) => audience.id ?? ""
          ),
          ...(destinationAudience?.id
            ? { targetAudienceId: destinationAudience?.id ?? "" }
            : { name: destinationAudienceName }),
        },
      });
      closeSnackbar();
      showMergeMessage({
        variant: "success",
        message: "Merged successfully!",
        description: "Your audiences have been merged successfully!",
        primaryCtaActionText: "View",
        primaryCtaActionFunction: () => {
          navigate.to(`/main/audience/sources/${newAudience.id}`, {
            state: {
              backUrl: { pathname: location.pathname, search: location.search },
            },
          });
          closeSnackbar();
        },
        secondaryCtaActionText: "Cancel",
        secondaryCtaActionFunction: () => {
          closeSnackbar();
        },
        persist: true,
      });
    } catch (e) {
      closeSnackbar();
      showMergeMessage({
        variant: "failed",
        message: "Merging error!",
        description:
          "It seems audience size is large for browser. Please contact the support team.",
        showClose: true,
      });
      return;
    }
  };
  const mergeDisabled = !destinationAudience?.id && !destinationAudienceName;

  return {
    onClose: closeMergeDialog,
    audiences,
    sourceAudiences,
    setSourceAudiences,
    destinationAudience,
    destinationAudienceName,
    destinationParentBoxRef,
    draggingItem,
    handleSourceDragOver,
    handleSourceDrop,
    handleSourceDragStart,
    handleDestinationDragStart,
    handleDestinationDragEnd,
    handleDestinationDragOver,
    handleDestinationDrop,
    setDestinationAudience,
    setDestinationAudienceName,
    sourceParentBoxRef,
    isAudienceMerging,
    handleMergeAudiences,
    mergeDisabled,
  };
}
